import bandhelData from "./bandhel.json";
import brandData from "./brand.json";
import decorData from "./decor.json";
import dimondData from "./dimond.json";
import gemsData from "./gems.json";
import goldData from "./gold.json";
import machineData from "./machine.json";
import pearlData from "./pearl.json";
import platinumData from "./platinum.json";
import silverData from "./silver.json";
import toolData from "./tool.json";

// Raw segments mapping
export const sellerProductData = {
  BANDHEL: bandhelData.BANDHEL || bandhelData,
  BRAND: brandData.BRAND || brandData,
  DECOR: decorData.DECOR || decorData,
  DIAMOND: dimondData.DIAMOND || dimondData,
  DIAMONDS: dimondData.DIAMOND || dimondData,
  GEMS: gemsData.GEMS || gemsData,
  GEM: gemsData.GEMS || gemsData,
  GOLD: goldData.GOLD || goldData,
  MACHINE: machineData.MACHINE || machineData,
  PEARL: pearlData.PEARL || pearlData,
  PEARLS: pearlData.PEARL || pearlData,
  PLATINUM: platinumData.PLATINUM || platinumData,
  SILVER: silverData.SILVER || silverData,
  TOOL: toolData.TOOLS || toolData.TOOL || toolData,
  TOOLS: toolData.TOOLS || toolData.TOOL || toolData,
};

// Canonical primary segments
export const PRIMARY_SEGMENTS = [
  "GOLD",
  "SILVER",
  "PLATINUM",
  "DIAMOND",
  "GEMS",
  "PEARL",
  "BANDHEL",
  "DECOR",
  "BRAND",
  "TOOLS",
  "MACHINE",
];

// Normalize segment keys (handling aliases like GEM/GEMS, TOOL/TOOLS, DIAMOND/DIAMONDS)
export const resolveSegmentKey = (segment) => {
  if (!segment) return "";
  const cleaned = String(segment).trim().toUpperCase();
  if (cleaned === "GEM" || cleaned === "GEMS") return "GEMS";
  if (cleaned === "PEARL" || cleaned === "PEARLS") return "PEARL";
  if (cleaned === "DIAMOND" || cleaned === "DIAMONDS") return "DIAMOND";
  if (cleaned === "TOOL" || cleaned === "TOOLS") return "TOOLS";
  return cleaned;
};

// Get segment data safely
export const getSegmentData = (segment) => {
  if (!segment) return {};
  const key = resolveSegmentKey(segment);
  return (
    sellerProductData[key] ||
    sellerProductData[String(segment).trim().toUpperCase()] ||
    {}
  );
};

// Map of categories by segment
export const categoriesBySegment = {};
Object.entries(sellerProductData).forEach(([seg, data]) => {
  categoriesBySegment[seg] =
    data && typeof data === "object" ? Object.keys(data) : [];
});

// Helper to get categories for a segment
export const getCategoriesForSegment = (segment) => {
  const segData = getSegmentData(segment);
  return Object.keys(segData);
};

// Helper to get subcategories (sources) for a segment and optional category
export const getSubcategoriesForSegment = (segment, category) => {
  const segData = getSegmentData(segment);
  if (!segData || typeof segData !== "object") return [];

  if (category && segData[category]) {
    return Object.keys(segData[category]);
  }

  if (category) {
    const matchedCat = Object.keys(segData).find(
      (k) => k.toUpperCase() === String(category).trim().toUpperCase(),
    );
    if (matchedCat && segData[matchedCat]) {
      return Object.keys(segData[matchedCat]);
    }
  }

  // Fallback: all unique subcategories across all categories in this segment
  const allSubs = new Set();
  Object.values(segData).forEach((catObj) => {
    if (catObj && typeof catObj === "object") {
      Object.keys(catObj).forEach((sub) => allSubs.add(sub));
    }
  });
  return Array.from(allSubs);
};

// Find a product node across segment, category, and source
export const findProduct = (segment, category, source, productName) => {
  if (!segment || !productName) return null;
  const segData = getSegmentData(segment);
  if (!segData || typeof segData !== "object") return null;

  const targetName = String(productName).trim().toUpperCase();

  // 1. Direct lookup if category & source provided
  if (category && source) {
    const catData =
      segData[category] ||
      Object.entries(segData).find(
        ([k]) => k.toUpperCase() === String(category).trim().toUpperCase(),
      )?.[1];
    if (catData && typeof catData === "object") {
      const srcData =
        catData[source] ||
        Object.entries(catData).find(
          ([k]) => k.toUpperCase() === String(source).trim().toUpperCase(),
        )?.[1];
      if (srcData && typeof srcData === "object") {
        if (srcData[productName]) return srcData[productName];
        const match = Object.entries(srcData).find(
          ([k]) => k.toUpperCase() === targetName,
        );
        if (match) return match[1];
      }
    }
  }

  // 2. Search within category across all sources
  if (category) {
    const catData =
      segData[category] ||
      Object.entries(segData).find(
        ([k]) => k.toUpperCase() === String(category).trim().toUpperCase(),
      )?.[1];
    if (catData && typeof catData === "object") {
      for (const [, srcVal] of Object.entries(catData)) {
        if (srcVal && typeof srcVal === "object") {
          if (srcVal[productName]) return srcVal[productName];
          const match = Object.entries(srcVal).find(
            ([k]) => k.toUpperCase() === targetName,
          );
          if (match) return match[1];
        }
      }
    }
  }

  // 3. Deep search across entire segment
  for (const [, catVal] of Object.entries(segData)) {
    if (catVal && typeof catVal === "object") {
      for (const [, srcVal] of Object.entries(catVal)) {
        if (srcVal && typeof srcVal === "object") {
          if (srcVal[productName]) return srcVal[productName];
          const match = Object.entries(srcVal).find(
            ([k]) => k.toUpperCase() === targetName,
          );
          if (match) return match[1];
        }
      }
    }
  }

  return null;
};

// Helper to get size options (supports options & dimensions)
export const getProductOptions = (segment, category, source, productName) => {
  const prod = findProduct(segment, category, source, productName);
  if (!prod) return [];
  return prod.options || prod.dimensions || [];
};

// Helper to get sizing unit
export const getProductSizingUnit = (segment, category, source, productName) => {
  const prod = findProduct(segment, category, source, productName);
  return prod?.sizing_unit || "";
};

// Re-export individual segment datasets
export {
  bandhelData,
  brandData,
  decorData,
  dimondData,
  gemsData,
  goldData,
  machineData,
  pearlData,
  platinumData,
  silverData,
  toolData,
};

// Legacy-compatible productData object
export const productData = {
  categoriesBySegment,
  productSources: (segment, category) =>
    getSubcategoriesForSegment(segment, category),
  getCategories: getCategoriesForSegment,
  getSubcategories: getSubcategoriesForSegment,
  findProduct,
  getProductOptions,
  getProductSizingUnit,
  ...sellerProductData,
};

export default sellerProductData;
